import React, { useState } from 'react';
import { MONTHS_RU, buildPlannerPeriodGrids, buildPlannerSections, exportPlannerWord, exportPlannerPdf } from '../utils/plannerExport';
import { holidaysAPI } from '../services/api';
import useDialog from '../hooks/useDialog';

const YEARS = [2024, 2025, 2026, 2027, 2028];

const holidayCache = new Map();
const HOLIDAYS_TIMEOUT_MS = 5000;

async function loadHolidays(startYear, endYear) {
  const set = new Set();
  const years = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);
  await Promise.allSettled(
    years.map(async (y) => {
      try {
        let data = holidayCache.get(y);
        if (!data) {
          data = await Promise.race([
            holidaysAPI.getHolidays(y),
            new Promise((_, reject) => setTimeout(() => reject(new Error('таймаут')), HOLIDAYS_TIMEOUT_MS))
          ]);
          holidayCache.set(y, data);
        }
        (data.holidays || []).forEach((d) => set.add(d));
      } catch (err) {
        console.warn('Не удалось получить праздничные дни за ' + y, err);
      }
    })
  );
  return set;
}

const FORMATS = [
  { id: 'word', label: 'Word (.docx)', hint: 'Табличный планёр как в документе' },
  { id: 'pdf', label: 'PDF', hint: 'Печатная версия A4' }
];

const MAX_PERIOD_MONTHS = 12;

export default function PlannerExportModal({ objects, userRole, defaultYear, defaultMonth, onClose }) {
  const managerObjects = objects.filter((o) => o && (o.events || o.name));
  const [startYear, setStartYear] = useState(defaultYear);
  const [startMonth, setStartMonth] = useState(defaultMonth);
  const [endYear, setEndYear] = useState(defaultYear);
  const [endMonth, setEndMonth] = useState(defaultMonth);
  const [objectId, setObjectId] = useState(() => {
    if (userRole === 'manager') return managerObjects[0] ? String(managerObjects[0].id) : 'all';
    return 'all';
  });
  const [format, setFormat] = useState('word');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const dialogRef = useDialog({ isOpen: true, onClose });

  const selectedObjectName = objectId === 'all' ? 'Все объекты' : (objects.find((o) => String(o.id) === objectId)?.name || '');

  const handleExport = async () => {
    setLoading(true);
    setError('');
    const start = startYear * 12 + startMonth;
    const end = endYear * 12 + endMonth;
    if (end < start) {
      setError('Конец периода не может быть раньше начала');
      setLoading(false);
      return;
    }
    if (end - start + 1 > MAX_PERIOD_MONTHS) {
      setError('Период не может превышать 12 месяцев (год)');
      setLoading(false);
      return;
    }
    try {
      const holidays = await loadHolidays(startYear, endYear);
      const args = objectId === 'all'
        ? {
            sections: buildPlannerSections(startYear, startMonth, endYear, endMonth, objects, holidays),
            mergedGrids: buildPlannerPeriodGrids(startYear, startMonth, endYear, endMonth, objects, null, holidays),
            objectName: 'Все объекты'
          }
        : {
            grids: buildPlannerPeriodGrids(startYear, startMonth, endYear, endMonth, objects, Number(objectId), holidays),
            objectName: selectedObjectName
          };
      if (format === 'word') {
        await exportPlannerWord(args);
      } else {
        await exportPlannerPdf(args);
      }
      onClose();
    } catch (e) {
      console.error(e);
      setError('Ошибка экспорта: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content schedule-modal planner-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="planner-modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <h2 id="planner-modal-title">Экспорт планёра</h2>
        <form onSubmit={(e) => { e.preventDefault(); handleExport(); }}>
          <fieldset className="form-group planner-row">
            <legend>Период (с — по)</legend>
            <div className="planner-period">
              <span className="planner-period-label">С</span>
              <select className="planner-period-year" aria-label="Год начала" value={startYear} onChange={(e) => setStartYear(Number(e.target.value))}>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="planner-period-month" aria-label="Месяц начала" value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
                {MONTHS_RU.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <span className="planner-period-label">по</span>
              <select className="planner-period-year" aria-label="Год окончания" value={endYear} onChange={(e) => setEndYear(Number(e.target.value))}>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="planner-period-month" aria-label="Месяц окончания" value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))}>
                {MONTHS_RU.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
            </div>
          </fieldset>
          <div className="form-group planner-row">
            <label htmlFor="planner-object">Объект</label>
            <select id="planner-object" value={objectId} onChange={(e) => setObjectId(e.target.value)}>
              {userRole !== 'manager' && <option value="all">Все объекты</option>}
              {objects.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <fieldset className="form-group planner-row">
            <legend>Формат</legend>
            <div className="planner-formats">
              {FORMATS.map((f) => (
                <label key={f.id} className={`planner-format ${format === f.id ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="plannerFormat"
                    value={f.id}
                    checked={format === f.id}
                    onChange={() => setFormat(f.id)}
                  />
                  <span className="planner-format-label">{f.label}</span>
                  <span className="planner-format-hint">{f.hint}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {error && <div className="planner-error" role="alert">{error}</div>}
          <div className="modal-buttons">
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? 'Формирование...' : 'Скачать планёр'}
            </button>
            <button type="button" className="cancel-button" onClick={onClose}>Отмена</button>
          </div>
        </form>
      </div>
    </div>
  );
}